import {IAutoOrderOrder} from '../models';

/**
 * Whether the order should appear under the Failed chip (any pipeline step failed).
 */
export function isAutoOrderListedInFailedChip(order: IAutoOrderOrder): boolean {
  return (
    order.status === 'FAILED'
    || order.emailStatus === 'FAILED'
    || order.iqStatus === 'FAILED'
    || order.pdfStatus === 'FAILED'
  );
}

/**
 * Whether the order failed at generation status or IQ submission and can be retried.
 */
export function isAutoOrderRetryableFailure(order: IAutoOrderOrder): boolean {
  return order.status === 'FAILED' || order.iqStatus === 'FAILED';
}

/**
 * Whether the order may be included in Failed-chip Delete All.
 * Matches selection delete, which blocks submitted and already-successful steps.
 */
export function isAutoOrderEligibleForFailedChipBulkDelete(order: IAutoOrderOrder): boolean {
  if (!isAutoOrderListedInFailedChip(order)) {
    return false;
  }
  if (
    order.status === 'SUBMITTED'
    || order.status === 'SUCCESS'
    || order.emailStatus === 'SENT'
    || order.pdfStatus === 'SUCCESS'
    || order.iqStatus === 'SUCCESS'
  ) {
    return false;
  }
  return true;
}

/**
 * Clears the retry lock when the pending order is absent from a refreshed list or no longer retryable.
 */
export function shouldClearPendingRetryAutoOrder(
  pendingOrderId: string | null,
  orders: IAutoOrderOrder[],
): boolean {
  if (!pendingOrderId) {
    return false;
  }
  const order: IAutoOrderOrder | undefined = orders.find(
    (candidate: IAutoOrderOrder): boolean => candidate.orderId === pendingOrderId,
  );
  if (!order) {
    return true;
  }
  return !isAutoOrderRetryableFailure(order);
}

/**
 * Applies the client-known Firestore update after a successful retry batch commit.
 */
export function patchAutoOrderAfterRetrySubmit(order: IAutoOrderOrder): IAutoOrderOrder {
  return {
    ...order,
    status: 'SUBMITTED',
  };
}

/**
 * Whether prepare / download-and-prepare may write PREPARED on this order.
 * Submitted orders (including IQ-failed recovery) must use retry, not re-prepare.
 */
export function canPrepareAutoOrder(order: IAutoOrderOrder): boolean {
  const status: string = order.status;
  return status !== 'SUBMITTED'
    && status !== 'SUCCESS'
    && status !== 'DELETING'
    && status !== 'EMPTY';
}

/**
 * Whether the user may open the order grid to fix item errors and retry.
 */
export function canOpenAutoOrderForRecovery(order: IAutoOrderOrder): boolean {
  const status: string = order.status;
  const iqStatus: string | undefined = order.iqStatus;

  if (status === 'EMPTY' || status === 'DELETING' || status === 'SUCCESS') {
    return false;
  }
  if (iqStatus === 'SUCCESS') {
    return false;
  }
  if (status === 'SUBMITTED') {
    return iqStatus === 'FAILED';
  }
  return true;
}
